import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  limit, 
  startAfter,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

export const productService = {
  // Get all products (for admin)
  getAllProducts: async (pageSize = 100, lastDoc = null) => {
    try {
      let q = query(
        collection(db, 'products'),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      const querySnapshot = await getDocs(q);
      const products = [];
      querySnapshot.forEach((doc) => {
        products.push({
          id: doc.id,
          ...doc.data()
        });
      });
      return products;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  // Get latest products for homepage
  getLatestProducts: async (limitCount = 6) => {
    try {
      const q = query(
        collection(db, 'products'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      const products = [];
      querySnapshot.forEach((doc) => {
        products.push({
          id: doc.id,
          ...doc.data()
        });
      });
      return products;
    } catch (error) {
      console.error('Error fetching latest products:', error);
      throw error;
    }
  },

  // Get product by ID
  getProductById: async (id) => {
    try {
      const docRef = doc(db, 'products', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Product not found');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  // Search products (by name)
  searchProducts: async (searchQuery, pageSize = 12) => {
    try {
      const q = query(
        collection(db, 'products'),
        where('name', '>=', searchQuery),
        where('name', '<=', searchQuery + '\uf8ff'),
        orderBy('name'),
        limit(pageSize)
      );
      const querySnapshot = await getDocs(q);
      const products = [];
      querySnapshot.forEach((doc) => {
        products.push({
          id: doc.id,
          ...doc.data()
        });
      });
      return products;
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  },

  // Create a new product
  createProduct: async (productData) => {
    try {
      const docRef = await addDoc(collection(db, 'products'), {
        ...productData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return {
        id: docRef.id,
        ...productData
      };
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  // Update a product
  updateProduct: async (id, productData) => {
    try {
      const docRef = doc(db, 'products', id);
      await updateDoc(docRef, {
        ...productData,
        updatedAt: serverTimestamp()
      });
      return {
        id,
        ...productData
      };
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  // Delete a product
  deleteProduct: async (id) => {
    try {
      await deleteDoc(doc(db, 'products', id));
      return { success: true };
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  // Update product stock
  updateProductStock: async (id, stockQuantity) => {
    try {
      const docRef = doc(db, 'products', id);
      await updateDoc(docRef, {
        stockQuantity,
        updatedAt: serverTimestamp()
      });
      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data()
      };
    } catch (error) {
      console.error('Error updating product stock:', error);
      throw error;
    }
  }
};

// -----------------------------------
// PRODUCT SERVICE 1 (FOR PAGINATION, FILTERS, CATEGORY)
// -----------------------------------
export const productService1 = {
  // Get products by page (with optional search)
  getProductsByPage: async (pageSize, pageNum, searchTerm = '') => {
    let q = query(
      collection(db, 'products'),
      orderBy('createdAt', 'desc')
    );

    let snapshot = await getDocs(q);
    let docs = snapshot.docs;

    if (searchTerm) {
      docs = docs.filter(doc => {
        const data = doc.data();
        return (
          data.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.category?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    const docsToSkip = (pageNum - 1) * pageSize;
    const paged = docs.slice(docsToSkip, docsToSkip + pageSize);
    return paged.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get total count (optionally for search)
  getProductsCount: async (searchTerm = '') => {
    const snapshot = await getDocs(collection(db, 'products'));
    if (!searchTerm) {
      return snapshot.size;
    }
    let docs = snapshot.docs;
    docs = docs.filter(doc => {
      const data = doc.data();
      return (
        data.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        data.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        data.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    return docs.length;
  },

  // Get products by category (with pagination and optional search)
  getProductsByCategory: async (category, pageSize, pageNum, searchTerm = '') => {
    let q = query(
      collection(db, 'products'),
      orderBy('createdAt', 'desc')
    );
    let snapshot = await getDocs(q);
    let docs = snapshot.docs;

    // Filter by category
    docs = docs.filter(doc => {
      const data = doc.data();
      return data.category === category;
    });

    // Further filter by search if needed
    if (searchTerm) {
      docs = docs.filter(doc => {
        const data = doc.data();
        return (
          data.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.category?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    const docsToSkip = (pageNum - 1) * pageSize;
    const paged = docs.slice(docsToSkip, docsToSkip + pageSize);
    return paged.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get count of products by category (with optional search)
  getProductsCountByCategory: async (category, searchTerm = '') => {
    let q = query(
      collection(db, 'products'),
      orderBy('createdAt', 'desc')
    );
    let snapshot = await getDocs(q);
    let docs = snapshot.docs;

    // Filter by category
    docs = docs.filter(doc => {
      const data = doc.data();
      return data.category === category;
    });

    // Further filter by search if needed
    if (searchTerm) {
      docs = docs.filter(doc => {
        const data = doc.data();
        return (
          data.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.category?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    return docs.length;
  }
};

export const promotionService = {
  // Get all promotions
  getAllPromotions: async (pageSize = 100, lastDoc = null) => {
    try {
      let q = query(
        collection(db, 'promotions'),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      const querySnapshot = await getDocs(q);
      const promotions = [];
      querySnapshot.forEach((doc) => {
        promotions.push({
          id: doc.id,
          ...doc.data()
        });
      });
      return promotions;
    } catch (error) {
      console.error('Error fetching promotions:', error);
      throw error;
    }
  },

  // Get promotion by ID
  getPromotionById: async (id) => {
    try {
      const docRef = doc(db, 'promotions', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Promotion not found');
      }
    } catch (error) {
      console.error('Error fetching promotion:', error);
      throw error;
    }
  },

  // Search promotions (by name)
  searchPromotions: async (searchQuery, pageSize = 12) => {
    try {
      const q = query(
        collection(db, 'promotions'),
        where('name', '>=', searchQuery),
        where('name', '<=', searchQuery + '\uf8ff'),
        orderBy('name'),
        limit(pageSize)
      );
      const querySnapshot = await getDocs(q);
      const promotions = [];
      querySnapshot.forEach((doc) => {
        promotions.push({
          id: doc.id,
          ...doc.data()
        });
      });
      return promotions;
    } catch (error) {
      console.error('Error searching promotions:', error);
      throw error;
    }
  },

  // Create a new promotion
  createPromotion: async (promotionData) => {
    try {
      const docRef = await addDoc(collection(db, 'promotions'), {
        ...promotionData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return {
        id: docRef.id,
        ...promotionData
      };
    } catch (error) {
      console.error('Error creating promotion:', error);
      throw error;
    }
  },

  // Update a promotion
  updatePromotion: async (id, promotionData) => {
    try {
      const docRef = doc(db, 'promotions', id);
      await updateDoc(docRef, {
        ...promotionData,
        updatedAt: serverTimestamp()
      });
      return {
        id,
        ...promotionData
      };
    } catch (error) {
      console.error('Error updating promotion:', error);
      throw error;
    }
  },

  // Delete a promotion
  deletePromotion: async (id) => {
    try {
      await deleteDoc(doc(db, 'promotions', id));
      return { success: true };
    } catch (error) {
      console.error('Error deleting promotion:', error);
      throw error;
    }
  },

  // Update promotion stock
  updatePromotionStock: async (id, stockQuantity) => {
    try {
      const docRef = doc(db, 'promotions', id);
      await updateDoc(docRef, {
        stockQuantity,
        updatedAt: serverTimestamp()
      });
      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data()
      };
    } catch (error) {
      console.error('Error updating promotion stock:', error);
      throw error;
    }
  }
};

// -----------------------------------
// PROMOTION SERVICE 1 (FOR PAGINATION, FILTERS, CATEGORY)
// -----------------------------------
export const promotionService1 = {
  // Get promotions by page (with optional search)
  getPromotionsByPage: async (pageSize, pageNum, searchTerm = '') => {
    let q = query(
      collection(db, 'promotions'),
      orderBy('createdAt', 'desc')
    );

    let snapshot = await getDocs(q);
    let docs = snapshot.docs;

    if (searchTerm) {
      docs = docs.filter(doc => {
        const data = doc.data();
        return (
          data.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.category?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    const docsToSkip = (pageNum - 1) * pageSize;
    const paged = docs.slice(docsToSkip, docsToSkip + pageSize);
    return paged.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get total count (optionally for search)
  getPromotionsCount: async (searchTerm = '') => {
    const snapshot = await getDocs(collection(db, 'promotions'));
    if (!searchTerm) {
      return snapshot.size;
    }
    let docs = snapshot.docs;
    docs = docs.filter(doc => {
      const data = doc.data();
      return (
        data.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        data.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        data.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    return docs.length;
  },

  // Get promotions by category (with pagination and optional search)
  getPromotionsByCategory: async (category, pageSize, pageNum, searchTerm = '') => {
    let q = query(
      collection(db, 'promotions'),
      orderBy('createdAt', 'desc')
    );
    let snapshot = await getDocs(q);
    let docs = snapshot.docs;

    // Filter by category
    docs = docs.filter(doc => {
      const data = doc.data();
      return data.category === category;
    });

    // Further filter by search if needed
    if (searchTerm) {
      docs = docs.filter(doc => {
        const data = doc.data();
        return (
          data.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.category?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    const docsToSkip = (pageNum - 1) * pageSize;
    const paged = docs.slice(docsToSkip, docsToSkip + pageSize);
    return paged.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get count of promotions by category (with optional search)
  getPromotionsCountByCategory: async (category, searchTerm = '') => {
    let q = query(
      collection(db, 'promotions'),
      orderBy('createdAt', 'desc')
    );
    let snapshot = await getDocs(q);
    let docs = snapshot.docs;

    // Filter by category
    docs = docs.filter(doc => {
      const data = doc.data();
      return data.category === category;
    });

    // Further filter by search if needed
    if (searchTerm) {
      docs = docs.filter(doc => {
        const data = doc.data();
        return (
          data.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.category?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    return docs.length;
  }
};

